import { useDelete, useGet, usePost, usePut } from '../../../sharedModules/hooks/useQuery';
import {
  AssignableUser,
  GroupNotificationSettings,
  NOTIFICATION_ROLES,
  OpenJob,
  RecipientPreview,
  WarehouseDepartmentGroup,
  warehouseAssignableUsersUrl,
  warehouseDepartmentGroupsUrl,
  warehouseGroupSettingsUrl,
  warehouseJobRecipientsUrl,
  warehouseOpenJobsUrl,
  warehouseRecipientPreviewUrl,
} from '../model';

export function useWarehouseDepartmentGroups(location: string) {
  return useGet<WarehouseDepartmentGroup[]>({ url: warehouseDepartmentGroupsUrl, params: { location } });
}

export function useAddGroupMembership() {
  return usePost();
}

export function addGroupMembershipVariables(userId: number, groupId: string, location: string) {
  return { url: warehouseDepartmentGroupsUrl, data: { user_id: userId, group_id: groupId, location } };
}

export function useRemoveGroupMembership() {
  return useDelete();
}

// location rides in the query string because useDelete sends no body: the backend scopes the
// delete to it so an admin in one location cannot remove the other location's row.
export function removeGroupMembershipVariables(membershipId: number, location: string) {
  return { url: `${warehouseDepartmentGroupsUrl}/${membershipId}?location=${encodeURIComponent(location)}` };
}

// Scoped to the location: each location notifies only its own employees.
export function useAssignableUsers(location: string) {
  return useGet<AssignableUser[]>({ url: warehouseAssignableUsersUrl, params: { location } });
}

export function useUpdateGroupSettings() {
  return usePut<GroupNotificationSettings>();
}

// The backend takes the notify_ columns; the page thinks in role keys. Translating here keeps
// that prefix out of every component that toggles a checkbox, and deriving it from the role
// table means adding a fifth role is not a two-file change.
export function updateGroupSettingsVariables(
  groupId: string,
  location: string,
  settings: GroupNotificationSettings,
) {
  return {
    url: warehouseGroupSettingsUrl(groupId),
    data: {
      location,
      settings: {
        mode: settings.mode,
        ...Object.fromEntries(NOTIFICATION_ROLES.map((role) => [`notify_${role.key}`, settings.roles[role.key]])),
      },
    },
  };
}

export function useAddJobRecipient() {
  return usePost();
}

export function addJobRecipientVariables(groupId: string, jobId: string, userId: number, location: string) {
  return { url: warehouseJobRecipientsUrl, data: { group_id: groupId, job_id: jobId, user_id: userId, location } };
}

export function useRemoveJobRecipient() {
  return useDelete();
}

export function removeJobRecipientVariables(recipientId: number, location: string) {
  return { url: `${warehouseJobRecipientsUrl}/${recipientId}?location=${encodeURIComponent(location)}` };
}

// Only fetched once an accordion opens: a group carries a few hundred open jobs, and there is
// no reason to pull every group's list to paint a collapsed page.
export function useOpenJobs(groupId: string, location: string, enabled: boolean) {
  return useGet<OpenJob[]>({
    url: warehouseOpenJobsUrl,
    params: { group_id: groupId, location },
    enabled,
  });
}

export function usePreviewRecipients() {
  return usePost<RecipientPreview>();
}

export function previewRecipientsVariables(poNumber: string, location: string) {
  return { url: warehouseRecipientPreviewUrl, data: { po_number: poNumber, location } };
}
